/* Facts a source's own page can state about the source, and the ones it deliberately cannot.
 *
 * **What this file no longer holds, and where it went.** `byAttention`, `tally` and the harvest and
 * last-checked readings are in `listing.ts`; the three capabilities are in `capabilities.ts`. Split
 * because the connections surface became two screens with different questions — a worklist that
 * ranks and a detail page that explains — and a single module holding both was where a change to the
 * ordering could quietly alter what the detail page claimed.
 *
 * **The absences below are the substance of the file.** The reference design's CONNECTION card lists
 * a Username, and `Connection` carries no such field: the credential lives in the secret store and
 * nothing in this platform's API returns any part of it — which is a property of the response type
 * rather than a rule the server follows, since the payload has no field a credential could serialise
 * into. So there is no username to render, and this file names that rather than leaving a blank cell
 * for a reader to interpret.
 */

import type { Connection } from '@/shared/api/generated/catalog';
import type { Health } from '@/shared/ui/Indicator';

/** The four states, mapped onto health. The distinction the API's own docstring insists on is kept. */
export const STATE_HEALTH = {
  /* `registered` is **not** a failure: nobody has tried to reach it yet. Collapsing it into `failed`
   * is the mistake `Connection.state`'s own documentation calls out, and it is the one a screen is
   * most likely to make because both are "not green". `unknown` is the honest tone — an absence of
   * evidence, which is what `--status-unknown` exists for. */
  registered: 'unknown',
  verified: 'healthy',
  failed: 'unavailable',
  /* Switched off deliberately. Not a fault, so not red — the same reasoning as a disabled serving
   * route reading as configuration rather than as an outage. */
  disabled: 'degraded',
} as const satisfies Record<Connection['state'], Health>;

export function healthOf(connection: Connection): Health {
  return STATE_HEALTH[connection.state];
}

/** What the CONNECTION card can state about the credential, which is never the credential.
 *
 * Three readings, and the third is why this is a function rather than a field on the card.
 *
 * `path` is the Vault path — echoed by the API because it is a path and not a secret, and shown here
 * because pointing at the wrong entry is by far the commonest registration mistake and a reader
 * cannot confirm they did not without seeing it.
 *
 * There is deliberately **no username reading**. The reference card has a Username row and this
 * platform has no username to put in it: the pair is sealed into the secret store at registration
 * and `Connection` has no field it could come back in. Rendering an empty row, or a dash, would
 * invite a reader to conclude the source has no user — so the row is absent and the card says
 * instead what is true of every source here, which is that the credential is stored server-side and
 * is never shown again.
 */
export interface CredentialFacts {
  readonly path: string;
  /** Whether the platform can ever show any part of the credential. Always `false`, and stated as a
   * value rather than assumed, so a future card cannot quietly start rendering one without changing
   * something a test reads. */
  readonly disclosesUsername: false;
}

export function credentialFacts(connection: Connection): CredentialFacts {
  return { path: connection.secret_ref, disclosesUsername: false };
}

/* Moved to `@/entities/connection`, because `catalog-browser` now renders a source too and one
 * feature may not import another's internals. Re-exported here so this feature's own files read
 * unchanged — the import direction (feature -> entity) is the permitted one. */
export { CONNECTOR_LABEL, addressOf } from '@/entities/connection';
